import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { setTitle, setMeta } from "@/lib/seo";
import { Loader2, Phone, MapPin, CheckCircle2 } from "lucide-react";

export default function GoLanding() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const route = params.get("route") || "";
  const drop = params.get("drop") || "";
  const src = params.get("src") || "postcard";
  const [biz, setBiz] = useState(null);
  const [state, setState] = useState("loading");
  const [form, setForm] = useState({ full_name: "", phone: "", email: "", inquiry: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let active = true;
    api.get(`/businesses/${slug}`).then(({ data }) => {
      if (!active) return;
      setBiz(data); setState("ok");
      setTitle(`${data.name} | Saving America Money`);
      setMeta("description", data.short_description || `Contact ${data.name}.`);
      api.post(`/go/${slug}/scan?route=${encodeURIComponent(route)}&drop=${encodeURIComponent(drop)}&src=${encodeURIComponent(src)}`).catch(() => {});
    }).catch(() => active && setState("missing"));
    return () => { active = false; };
  }, [slug, route, drop, src]);

  const submit = async () => {
    if (!form.phone.trim() && !form.email.trim()) return toast.error("Add a phone or email so they can reach you");
    setSending(true);
    try {
      await api.post(`/businesses/${slug}/contact`, {
        ...form, source: "website_form", tags: ["Postcard"],
        utm_source: "postcard", utm_medium: src, utm_campaign: route ? `route-${route}` : "postcard",
        landing_url: window.location.href, route, drop,
      });
      setDone(true);
    } catch { toast.error("Could not send — please try again"); }
    finally { setSending(false); }
  };

  if (state === "loading") return <div className="py-32 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-sam-crimson" /></div>;
  if (state === "missing") return <div className="py-28 text-center text-sam-navy font-display text-xl" data-testid="go-missing">Business not found.</div>;

  const phone = biz.tracked_phone || biz.phone;

  return (
    <div className="mx-auto max-w-xl px-5 py-12" data-testid="go-landing">
      <div className="rounded-3xl border border-border bg-card p-7 shadow-sm">
        {biz.logo_url && <img src={biz.logo_url} alt={biz.name} className="h-16 w-16 rounded-xl object-cover mb-4" />}
        <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-sam-crimson">Special offer</span>
        <h1 className="mt-2 font-display font-black text-3xl text-sam-navy leading-tight" data-testid="go-business-name">{biz.name}</h1>
        {biz.postcard_deal_headline && <p className="mt-2 text-lg font-semibold text-sam-navy">{biz.postcard_deal_headline}</p>}
        {biz.postcard_deal_details && <p className="mt-1 text-sm text-sam-slate">{biz.postcard_deal_details}</p>}
        {biz.short_description && <p className="mt-3 text-sm text-sam-slate">{biz.short_description}</p>}
        <div className="mt-3 flex flex-wrap gap-4 text-sm text-sam-slate">
          {(biz.city || biz.state_abbr) && <span className="inline-flex items-center gap-1.5"><MapPin className="h-4 w-4" />{biz.city}{biz.state_abbr ? `, ${biz.state_abbr}` : ""}</span>}
          {phone && <a href={`tel:${phone}`} className="inline-flex items-center gap-1.5 font-semibold text-sam-navy" data-testid="go-phone"><Phone className="h-4 w-4 text-sam-crimson" />{phone}</a>}
        </div>

        {done ? (
          <div className="mt-6 rounded-2xl bg-sam-green/10 border border-sam-green/30 p-6 text-center" data-testid="go-success">
            <CheckCircle2 className="h-10 w-10 text-sam-green mx-auto" />
            <p className="mt-3 font-semibold text-sam-navy">Thanks! {biz.name} will reach out shortly.</p>
          </div>
        ) : (
          <div className="mt-6 space-y-3" data-testid="go-form">
            <h2 className="font-display font-bold text-sam-navy text-base">Request a quote</h2>
            <div><Label>Your name</Label><Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="go-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="go-form-phone" /></div>
              <div><Label>Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="go-email" /></div>
            </div>
            <div><Label>What do you need?</Label><Textarea rows={3} value={form.inquiry} onChange={(e) => setForm({ ...form, inquiry: e.target.value })} data-testid="go-inquiry" /></div>
            <Button className="w-full bg-sam-crimson hover:bg-sam-crimson/90" onClick={submit} disabled={sending} data-testid="go-submit">
              {sending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Send my request
            </Button>
          </div>
        )}
      </div>
      <p className="mt-4 text-center text-xs text-sam-slate">Powered by Saving America Money</p>
    </div>
  );
}
